// Create wind trade modal — mirrors legacy showWindCreateModal + calcWindQty.
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { API_BASE, apiPost } from "@/lib/api-client";
import { fmt } from "@/lib/format";
import Modal from "@/components/common/modal";

interface PreCreateAdvice {
  ok: boolean;
  dir: "long" | "short";
  conf: number;
  regime: "T" | "R" | "V" | "Q";
  why: string;
  risks: string[];
  params: Record<string, any> | null;
}
const REGIME_LABEL: Record<string, string> = { T: "trending", R: "ranging", V: "volatile", Q: "quiet" };

// Legacy calcWindQty fetches the OKX public ticker directly — mirror that so
// the backend doesn't need a dedicated "what price am I looking at" route.
async function fetchSymbolPrice(symbol: string): Promise<number> {
  const instId = symbol.replace("/", "-").replace(":USDT", "-SWAP");
  const res = await fetch(`https://www.okx.com/api/v5/market/ticker?instId=${instId}`);
  if (!res.ok) return 0;
  const json = (await res.json()) as { data?: { last?: string }[] };
  return parseFloat(json.data?.[0]?.last ?? "0") || 0;
}

interface CreateWindModalProps {
  onClose: () => void;
}

const inputCls =
  "w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors";
const primaryBtn =
  "text-xs px-4 py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50";
const ghostBtn =
  "text-xs px-4 py-2 rounded border border-border text-muted hover:text-text transition-colors";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">{label}</label>
      {children}
      {hint && <div className="text-[0.65rem] text-muted mt-1">{hint}</div>}
    </div>
  );
}

export default function CreateWindModal({ onClose }: CreateWindModalProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [symbol, setSymbol] = useState("SOL/USDT:USDT");
  const [margin, setMargin] = useState("5");
  const [leverage, setLeverage] = useState("10");
  const [tpPct, setTpPct] = useState("0.5");
  const [slPct, setSlPct] = useState("0.3");
  const [holdMin, setHoldMin] = useState("5");
  const [forceMin, setForceMin] = useState("30");
  const [autoReopen, setAutoReopen] = useState(true);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiAutoAct, setAiAutoAct] = useState(false);

  const [symbolPrice, setSymbolPrice] = useState<number>(0);
  const [qtyDisplay, setQtyDisplay] = useState("—");
  const [feeWarn, setFeeWarn] = useState("");
  const [aiAdvice, setAiAdvice] = useState<PreCreateAdvice | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Recalculate qty whenever margin / leverage / symbol price changes
  useEffect(() => {
    const m = parseFloat(margin) || 0;
    const lev = parseInt(leverage) || 10;
    if (!m || !symbolPrice) {
      setQtyDisplay(symbolPrice ? "—" : "price unavailable");
      return;
    }
    const qty = (m * lev) / symbolPrice;
    setQtyDisplay(`${qty.toFixed(6)} @ ${fmt(symbolPrice, symbol)}`);
  }, [margin, leverage, symbolPrice, symbol]);

  // Fetch price on mount and whenever the user blurs a new symbol.
  async function fetchPrice(sym: string) {
    if (!sym) return;
    const price = await fetchSymbolPrice(sym);
    setSymbolPrice(price);
    if (!price) setQtyDisplay("price unavailable");
  }

  useEffect(() => {
    void fetchPrice(symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fee warning when TP% very small
  useEffect(() => {
    const tp = parseFloat(tpPct);
    if (tp && tp < 0.1) {
      setFeeWarn(`TP ${tp}% may be less than round-trip fees (~0.08%). Likely loss.`);
    } else {
      setFeeWarn("");
    }
  }, [tpPct]);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!symbol || !margin) throw new Error("Symbol and margin required");
      if (!symbolPrice) throw new Error("Price not loaded yet, try again");
      const m = parseFloat(margin);
      const lev = parseInt(leverage);
      const quantity = (m * lev) / symbolPrice;
      return apiPost<{ session?: { id: string }; latest_cycle?: { direction?: string }; fee_warning?: string }>("/wind", {
        symbol,
        quantity,
        margin: m,
        tp_pct: parseFloat(tpPct),
        sl_pct: parseFloat(slPct),
        leverage: lev,
        hold_minutes: parseInt(holdMin),
        force_minutes: parseInt(forceMin),
        auto_reopen: autoReopen,
        ai_enabled: aiEnabled,
        ai_auto_act: aiAutoAct,
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["wind"] });
      onClose();
      if (data.session?.id) {
        navigate(`/wind/${data.session.id}`);
      }
    },
    onError: (e: Error) => alert(e.message || "Failed to create"),
  });

  return (
    <Modal open title="New Wind Trade" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Symbol">
          <input
            className={inputCls}
            value={symbol}
            onChange={(e) => { setSymbol(e.target.value); setSymbolPrice(0); }}
            onBlur={() => fetchPrice(symbol)}
            placeholder="SOL/USDT:USDT"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Margin (USDT)">
            <input
              className={inputCls}
              type="number"
              step="any"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              placeholder="5"
            />
          </Field>
          <Field label="Leverage">
            <input
              className={inputCls}
              type="number"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              placeholder="10"
            />
          </Field>
        </div>

        <Field label="Order Size (base)" hint="qty = margin × leverage / price">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[0.7rem] text-info font-semibold">{qtyDisplay}</span>
          </div>
          <input
            className={`${inputCls} text-muted`}
            readOnly
            placeholder="auto from margin"
            value={symbolPrice ? ((parseFloat(margin) || 0) * (parseInt(leverage) || 10) / symbolPrice).toFixed(8) : ""}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="TP %">
            <input
              className={inputCls}
              type="number"
              step="any"
              value={tpPct}
              onChange={(e) => setTpPct(e.target.value)}
              placeholder="0.5"
            />
          </Field>
          <Field label="SL %">
            <input
              className={inputCls}
              type="number"
              step="any"
              value={slPct}
              onChange={(e) => setSlPct(e.target.value)}
              placeholder="0.3"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Hold (min)">
            <input
              className={inputCls}
              type="number"
              value={holdMin}
              onChange={(e) => setHoldMin(e.target.value)}
              placeholder="1"
            />
          </Field>
          <Field label="Force exit (min)">
            <input
              className={inputCls}
              type="number"
              value={forceMin}
              onChange={(e) => setForceMin(e.target.value)}
              placeholder="60"
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 cursor-pointer text-sm text-dim">
          <input
            type="checkbox"
            checked={autoReopen}
            onChange={(e) => setAutoReopen(e.target.checked)}
            className="accent-accent"
          />
          Auto re-open
        </label>

        {/* AI settings — stacked vertically with descriptions */}
        <div className="space-y-2 pt-2 border-t border-border">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-dim">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => {
                setAiEnabled(e.target.checked);
                if (!e.target.checked) setAiAutoAct(false);
              }}
              className="accent-accent mt-0.5"
            />
            <span className="flex-1">
              <span className="font-medium text-text">Enable AI advisor</span>
              <span className="block text-[0.72rem] text-muted">
                Sends market data to AI; advice appears in UI (advisory only).
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 text-sm text-dim ${
              aiEnabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={aiAutoAct}
              onChange={(e) => setAiAutoAct(e.target.checked)}
              className="accent-accent mt-0.5"
              disabled={!aiEnabled}
            />
            <span className="flex-1">
              <span className="font-medium text-text">AI auto-act</span>
              <span className="block text-[0.72rem] text-muted">
                AI automatically executes decisions (cut loss, skip cycle, adjust TP/SL).
              </span>
            </span>
          </label>
          {aiAutoAct && aiEnabled && (
            <div className="text-[0.72rem] text-warn bg-warn/10 rounded px-2 py-1.5">
              <span className="opacity-85 mr-1">⚠</span>
              AI will execute decisions automatically — verify conf thresholds before live use.
            </div>
          )}
        </div>

        {feeWarn && (
          <div className="text-[0.75rem] text-warn bg-warn/10 rounded px-2 py-1.5">
            {feeWarn}
          </div>
        )}

        {aiError && <div className="text-[0.75rem] text-danger">{aiError}</div>}

        {aiAdvice && (
          <div className={`rounded border px-3 py-2 space-y-1.5 text-xs ${aiAdvice.ok ? "border-success/40 bg-success/5" : "border-warn/40 bg-warn/5"}`}>
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${aiAdvice.ok ? "text-success" : "text-warn"}`}>
                {aiAdvice.ok ? `OK to open ${aiAdvice.dir.toUpperCase()}` : "Reconsider"}
              </span>
              <span className="text-muted">conf {Math.round(aiAdvice.conf * 100)}% · {REGIME_LABEL[aiAdvice.regime] ?? aiAdvice.regime}</span>
            </div>
            {aiAdvice.why && <div className="text-dim">{aiAdvice.why}</div>}
            {aiAdvice.risks?.length > 0 && (
              <ul className="text-warn space-y-0.5">
                {aiAdvice.risks.map((r, i) => <li key={i}>⚠ {r}</li>)}
              </ul>
            )}
            {aiAdvice.params && (() => {
              const p = aiAdvice.params;
              const hasTp = typeof p.tp === "number" && p.tp > 0;
              const hasSl = typeof p.sl === "number" && p.sl > 0;
              const hasHold = typeof p.hold === "number" && p.hold > 0;
              const hasForce = typeof p.force === "number" && p.force > 0;
              const any = hasTp || hasSl || hasHold || hasForce;
              if (!any) return null;
              return (
                <div className="flex flex-col gap-1 pt-1 border-t border-border/40">
                  <div className="text-[0.68rem] text-muted uppercase tracking-wider">Suggested params</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.75rem]">
                    {hasTp && <span>TP <span className="text-success">{p.tp}%</span> <span className="text-dim">(was {tpPct}%)</span></span>}
                    {hasSl && <span>SL <span className="text-danger">{p.sl}%</span> <span className="text-dim">(was {slPct}%)</span></span>}
                    {hasHold && <span>Hold <span className="text-accent">{p.hold}m</span> <span className="text-dim">(was {holdMin}m)</span></span>}
                    {hasForce && <span>Force <span className="text-accent">{p.force}m</span> <span className="text-dim">(was {forceMin}m)</span></span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (hasTp) setTpPct(String(p.tp));
                      if (hasSl) setSlPct(String(p.sl));
                      if (hasHold) setHoldMin(String(p.hold));
                      if (hasForce) setForceMin(String(p.force));
                      setAiAdvice(null);
                    }}
                    className={`${ghostBtn} !text-xs !py-1 self-start mt-0.5`}
                  >
                    Apply all
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            className={primaryBtn}
            disabled={createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? "Creating…" : "Create"}
          </button>
          <button
            type="button"
            onClick={async () => {
              setAiError("");
              setAiAdvice(null);
              setAiLoading(true);
              try {
                const res = await fetch(`${API_BASE}/wind/ai-pre-create`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    symbol,
                    tp_pct: parseFloat(tpPct),
                    sl_pct: parseFloat(slPct),
                    hold_minutes: parseInt(holdMin),
                    force_minutes: parseInt(forceMin),
                    leverage: parseInt(leverage),
                  }),
                });
                if (res.status === 503) { setAiError("AI not configured on server"); return; }
                if (!res.ok) {
                  const b = await res.json().catch(() => ({})) as { error?: string };
                  throw new Error(b.error ?? res.statusText);
                }
                setAiAdvice(await res.json() as PreCreateAdvice);
              } catch (e) { setAiError((e as Error).message); }
              finally { setAiLoading(false); }
            }}
            disabled={aiLoading || createMut.isPending}
            className={ghostBtn}
          >
            {aiLoading ? "Reviewing…" : "Review with AI"}
          </button>
          <button className={ghostBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
